import {Button} from '../modules/button.js';

const DOCUMENT = document;
const CSS_ERROR_CLASS = 'error';
const VALIDATION_CHECK_TIMEOUT = 700;
const ERROR_FIELD_SELECTOR = 'form .form__field.error, form .group.error';
/** @type {ReturnType<typeof setTimeout>|undefined} */
let scrollToErrorTimeoutId;

/**
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} field
 */
function validateFormField(field) {
    if (! field.checkValidity()) {
        return;
    }

    let parent = field.closest('.group');

    if (parent instanceof HTMLElement) {// in rare cases, parent.parentNode is null (detached from DOM)
        parent.classList.remove(CSS_ERROR_CLASS);
    }

    field.setAttribute('aria-invalid', 'false');
    field.labels?.forEach((label) => {
        label.classList.remove(CSS_ERROR_CLASS);
    });

    const form = field.form;
    if (! (form instanceof HTMLFormElement)) return;

    if ('dynamicSubmit' in form.dataset) {
        form.querySelectorAll('[type="submit"]').forEach((submitButton) => {
            if (submitButton.form === form) {
                // use private field to ignore global listener for 'invalid'
                // event because Form#checkValidity() triggers 'invalid'
                // for every failing form child element
                form._ignoreInvalidListener = true;

                if (form.checkValidity()) {
                    submitButton.disabled = false;
                }

                delete form._ignoreInvalidListener;
            }
        });
    }
}

DOCUMENT.addEventListener(
    'invalid',
    (event) => {
        /** @type {HTMLInputElement|HTMLSelectElement} */
        const field = event.target;
        // ignore elements with disabled validation
        if (field.hasAttribute('formnovalidate')) return;

        const form = field.form;
        // skip elements without a parent form
        // ignore forms with disabled validation
        if (!form || form.hasAttribute('novalidate') || form._ignoreInvalidListener) return;
        // disable native browser validation popovers
        if (form.method !== 'DIALOG') {
            event.preventDefault();
        }

        let parent = field.closest('.group');

        if (parent instanceof HTMLElement) {// in rare cases, parent.parentNode is null (detached from DOM)
            parent.classList.add(CSS_ERROR_CLASS);
        }

        field.setAttribute('aria-invalid', 'true');
        field.labels?.forEach((label) => {
            label.classList.add(CSS_ERROR_CLASS);
        });

        // Automatically clear button loading when native validation blocks the submit.
        form.querySelectorAll('.button--loading').forEach((button) => {
            if (button instanceof HTMLElement) {
                Button.removeLoading(button);
            }
        });

        if ('dynamicSubmit' in form.dataset) {
            form.querySelectorAll('[type="submit"]').forEach((submitButton) => {
                if (submitButton.form === form) {
                    submitButton.disabled = true;
                }
            });
        }

        // Scroll to first error field after all invalid events in this cycle have fired
        clearTimeout(scrollToErrorTimeoutId);
        scrollToErrorTimeoutId = setTimeout(scrollToErrorInputOnPageLoad, 0);
    },
    true
);

DOCUMENT.addEventListener(
    'input',
    (event) => {
        /** @type {HTMLFormElement} */
        const formElement = event.target;
        /**
         * TODO: 'rt-editor' web component is not form-associated, causing TypeError due to missing 'hasAttribute'.
         * See /resources/js/components/ratingInput.js for a similar fix with a polyfill.
         * The issue exist in old Safari versions
         */
        // ignore elements with disabled validation
        if (formElement.hasAttribute('formnovalidate')) return;

        const form = formElement.form;
        // skip elements without a parent form
        // ignore forms with disabled validation
        if (!form || form.hasAttribute('novalidate')) return;

        const lastTimeoutId = formElement._validationTimeoutId;
        if (lastTimeoutId) clearTimeout(lastTimeoutId);
        formElement._validationTimeoutId = setTimeout(() => {
            clearTimeout(formElement._validationTimeoutId);
            delete formElement._validationTimeoutId;
            validateFormField(formElement);
        }, VALIDATION_CHECK_TIMEOUT);
    },
    true
);

DOCUMENT.addEventListener(
    'change',
    (event) => {
        const formElement = event.target;
        // ignore elements with disabled validation
        if (formElement.hasAttribute('formnovalidate')) return;

        const form = formElement.form;
        // skip elements without a parent form
        // ignore forms with disabled validation
        if (!form || form.hasAttribute('novalidate')) return;

        validateFormField(formElement);
    },
    true
);

/**
 * @returns {HTMLElement|null}
 */
function getFirstErrorContainer() {
    const errorContainer = DOCUMENT.querySelector(ERROR_FIELD_SELECTOR);

    return errorContainer instanceof HTMLElement ? errorContainer : null;
}

/**
 * @param {HTMLElement} errorContainer
 * @returns {HTMLElement|null}
 */
function getErrorFocusTarget(errorContainer) {
    const preferredTarget = errorContainer.querySelector([
        '[data-tags-text-input]:not([disabled])',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
    ].join(', '));

    if (preferredTarget instanceof HTMLElement) {
        return preferredTarget;
    }

    const fallbackTarget = errorContainer.querySelector('button:not([disabled]), [tabindex]:not([tabindex="-1"])');

    return fallbackTarget instanceof HTMLElement ? fallbackTarget : null;
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isElementWithinViewport(element) {
    const rect = element.getBoundingClientRect();

    return rect.top >= 0 && rect.bottom <= window.innerHeight;
}

/** Scrolls to the first form field with an error on page load to improve UX after validation failures */
function scrollToErrorInputOnPageLoad() {
    const errorContainer = getFirstErrorContainer();
    if (!(errorContainer instanceof HTMLElement)) {
        return;
    }

    const focusTarget = getErrorFocusTarget(errorContainer);
    const scrollTarget = focusTarget ?? errorContainer;

    if (!isElementWithinViewport(errorContainer)) {
        scrollTarget.scrollIntoView({block: 'center', behavior: 'smooth'});
    }

    if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({preventScroll: true});
    }
}

/**
 *
 */
function scheduleScrollToErrorInputOnPageLoad() {
    requestAnimationFrame(() => {
        window.setTimeout(scrollToErrorInputOnPageLoad, 50);
    });
}

document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', scheduleScrollToErrorInputOnPageLoad)
    : scheduleScrollToErrorInputOnPageLoad();

window.addEventListener('load', scheduleScrollToErrorInputOnPageLoad);
window.addEventListener('pageshow', scheduleScrollToErrorInputOnPageLoad);
